// frontend/src/pages/VehicleMarketplacePage.js
import React, { useState, useEffect } from 'react';
import { vehiclesApi } from '../api/vehicles';
import VehicleCard from '../components/vehicle/VehicleCard';
import { FaSearch, FaCar, FaFilter, FaSortAmountDown, FaSpinner } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/admin';

// Price Range Config
const MIN_PRICE_LIMIT = 0;
const MAX_PRICE_LIMIT = 100000000; // 100 Million

function VehicleMarketplacePage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter States
  const [filters, setFilters] = useState({
    make: '',
    condition: ''
  });
  
  // Price State
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [localPrice, setLocalPrice] = useState({ min: MIN_PRICE_LIMIT, max: MAX_PRICE_LIMIT });
  const [sortBy, setSortBy] = useState('newest');
  
  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchVehicles = async (isSearch = false) => {
    setLoading(true);
    setError(null);
    try {
      const queryFilters = {
          ...filters,
          q: searchTerm,
          min_price: priceRange.min,
          max_price: priceRange.max,
          sort_by: sortBy
      };
      
      const data = await vehiclesApi.getAllVehicles(queryFilters);
      setVehicles(data);

      if (isSearch && searchTerm.trim()) {
          adminApi.logSearch(searchTerm, data.length);
      }
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      setError("Failed to load vehicles. If filtering by price, check backend terminal for index link.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load & Filter Trigger
  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, priceRange, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVehicles(true);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // --- PRICE HANDLERS ---
  const handleSliderChange = (e) => {
      const { name, value } = e.target;
      const val = parseInt(value);
      if (name === 'min') {
          if (val < localPrice.max) setLocalPrice(prev => ({ ...prev, min: val }));
      } else {
          if (val > localPrice.min) setLocalPrice(prev => ({ ...prev, max: val }));
      }
  };

  const handlePriceInputChange = (e) => {
      const { name, value } = e.target;
      setLocalPrice(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyPrice = () => {
      setPriceRange({ 
          min: localPrice.min > MIN_PRICE_LIMIT ? localPrice.min : null, 
          max: localPrice.max < MAX_PRICE_LIMIT ? localPrice.max : null 
      });
  };

  const clearFilters = () => {
    setFilters({ make: '', condition: '' });
    setLocalPrice({ min: MIN_PRICE_LIMIT, max: MAX_PRICE_LIMIT });
    setPriceRange({ min: null, max: null });
    setSortBy('newest');
    setSearchTerm('');
  };

  // Calculate percentage for slider track background
  const minPercent = ((localPrice.min - MIN_PRICE_LIMIT) / (MAX_PRICE_LIMIT - MIN_PRICE_LIMIT)) * 100;
  const maxPercent = ((localPrice.max - MIN_PRICE_LIMIT) / (MAX_PRICE_LIMIT - MIN_PRICE_LIMIT)) * 100;

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4">
        
        {/* Header Section - VERTICALLY ALIGNED */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div className="text-center md:text-left w-full md:w-auto">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center justify-center md:justify-start gap-2">
              <FaCar className="text-primary" /> Vehicle Marketplace
            </h1>
            <p className="text-gray-600 mt-1 text-center md:text-left">Find your dream car or sell your own.</p>
          </div>
          
          {/* Search Bar (Desktop) */}
          <form onSubmit={handleSearch} className="hidden md:flex relative w-full max-w-md mx-4">
             <input 
                type="text" 
                placeholder="Search vehicles..." 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
             />
             <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </form>

          <Link 
            to="/vehicles/sell" 
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-md transition duration-300 w-full md:w-auto text-center flex items-center justify-center h-full"
          >
            Sell Your Car
          </Link>
        </div>

        {/* Mobile Search Bar */}
        <form onSubmit={handleSearch} className="md:hidden relative w-full mb-4">
             <input 
                type="text" 
                placeholder="Search vehicles..." 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
             />
             <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </form>

        <div className="flex flex-col lg:flex-row gap-8">
            
            {/* --- SIDEBAR FILTERS --- */}
            <div className={`lg:w-1/4 bg-white p-5 rounded-lg shadow-sm h-fit ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2"><FaFilter /> Filters</h3>
                    <button onClick={clearFilters} className="text-xs text-red-500 hover:underline">Clear All</button>
                </div>

                {/* SORTING */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider flex items-center gap-2">
                        <FaSortAmountDown /> Sort By
                    </h4>
                    <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full p-2 border rounded text-sm bg-gray-50"
                    >
                        <option value="newest">Newest Arrivals</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                    </select>
                </div>

                {/* MAKE */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                    <select 
                        name="make" 
                        value={filters.make} 
                        onChange={handleFilterChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none bg-white text-sm"
                    >
                        <option value="">All Makes</option>
                        <option value="Toyota">Toyota</option>
                        <option value="Honda">Honda</option>
                        <option value="Lexus">Lexus</option>
                        <option value="Mercedes-Benz">Mercedes-Benz</option>
                        <option value="Ford">Ford</option>
                        <option value="Hyundai">Hyundai</option>
                        <option value="Kia">Kia</option>
                    </select>
                </div>

                {/* CONDITION */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <select 
                        name="condition" 
                        value={filters.condition} 
                        onChange={handleFilterChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none bg-white text-sm"
                    >
                        <option value="">All Conditions</option>
                        <option value="nigerian_used">Nigerian Used</option>
                        <option value="foreign_used">Foreign Used</option>
                        <option value="new">Brand New</option>
                    </select>
                </div>

                {/* PRICE RANGE (SLIDER + INPUTS) */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider">Price Range (NGN)</h4>
                    
                    {/* Slider */}
                    <div className="range-slider mb-4">
                        <div 
                            className="track" 
                            style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
                        ></div>
                        <input 
                            type="range" 
                            name="min" 
                            min={MIN_PRICE_LIMIT} 
                            max={MAX_PRICE_LIMIT} 
                            value={localPrice.min} 
                            onChange={handleSliderChange} 
                        />
                        <input 
                            type="range" 
                            name="max" 
                            min={MIN_PRICE_LIMIT} 
                            max={MAX_PRICE_LIMIT} 
                            value={localPrice.max} 
                            onChange={handleSliderChange} 
                        />
                    </div>

                    {/* Manual Inputs */}
                    <div className="flex gap-2 mb-3">
                        <input 
                            type="number" 
                            name="min"
                            placeholder="Min" 
                            value={localPrice.min}
                            onChange={handlePriceInputChange}
                            className="w-1/2 p-2 border rounded text-sm"
                        />
                        <input 
                            type="number" 
                            name="max"
                            placeholder="Max" 
                            value={localPrice.max}
                            onChange={handlePriceInputChange}
                            className="w-1/2 p-2 border rounded text-sm"
                        />
                    </div>

                    {/* APPLY BUTTON */}
                    <button 
                        onClick={handleApplyPrice}
                        className="w-full bg-primary text-white text-xs font-bold py-2 rounded hover:bg-primary-dark transition-colors shadow-sm flex justify-center items-center gap-2"
                    >
                        {loading ? <FaSpinner className="animate-spin" /> : 'Apply Price Filter'}
                    </button>
                </div>
            </div>

            {/* --- MAIN CONTENT --- */}
            <div className="lg:w-3/4">
                
                {/* Mobile Filter Toggle */}
                <button 
                    className="lg:hidden w-full bg-white border border-gray-300 px-4 py-3 rounded-lg text-gray-700 flex items-center justify-center gap-2 mb-4 font-bold shadow-sm"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FaFilter /> {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>

                {/* Content Area */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-center">
                        {error}
                    </div>
                ) : vehicles.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                        <FaCar className="text-gray-300 text-6xl mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No vehicles found</h3>
                        <p className="text-gray-600 mb-6">Try adjusting your filters or check back later.</p>
                        <button onClick={clearFilters} className="text-primary hover:underline font-medium">
                        View All Vehicles
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {vehicles.map((vehicle) => (
                        <VehicleCard key={vehicle.id} vehicle={vehicle} />
                        ))}
                    </div>
                )}
            </div>
        </div>

      </div>
    </div>
  );
}

export default VehicleMarketplacePage;